import React, { use, useMemo } from "react";
import VStack from "../../ui_atoms/VStack";
import { StyleSheet } from "react-native";
import { Document, ActivityType, DocumentType } from "../../__generated__/graphql";
import { DisplayableDocument } from "../../contexts/DocumentManager";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import { Buildable } from "ts-essentials";
import SelectField from "../SelectField";
import { DEFAULT_HOURS_PER_WEEK, formatActivityTypeTitle, MissionWithIjm } from "../../utils";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { FIND_ACTIVITY_TYPE, GET_PRIVATE_CLIENT_ACTIVITY_TYPES } from "../../graphql/core";
import Callout from "../../ui_atoms/Callout";
import { BodyMediumText } from "../FormattedText";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import ListItem from "../../ui_atoms/ListItem";
import DocumentButton from "../document/DocumentButton";
import Gap from "../../ui_atoms/Gap";
import { DocumentContext } from "../../contexts/DocumentManager";
import { v4 as uuidv4 } from "uuid";
import { useCatchLazyQueryError } from "../../hooks/errors";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { allCCTState } from "../../atoms/core";
import { useAtomValue } from "jotai";
import { getIjmSelection } from "../../utils/ijm-utils";

type MissionJobTitleProps = {
    mission: Buildable<MissionWithIjm>;
    setMission: (
        mission:
            | Buildable<MissionWithIjm>
            | ((prev: Buildable<MissionWithIjm>) => Buildable<MissionWithIjm>),
    ) => void;
};

const MissionJobTitle: React.FC<MissionJobTitleProps> = ({ mission, setMission }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { colors, gridSize } = useTheme();

    const { editDocument } = use(DocumentContext);
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const allCCT = useAtomValue(allCCTState);
    const workerGender = mission.workerInfo?.gender;
    const [getActivityType, { error }] = useLazyQuery(FIND_ACTIVITY_TYPE);
    const privateClientActivityTypes = useQuery(GET_PRIVATE_CLIENT_ACTIVITY_TYPES, {
        skip: !isPrivateClient,
        variables: {
            stateCode: mission.currentContract?.workingAddress?.locality?.state?.code,
            legalEmployerId: mission.legalEmployer?.id,
        },
    });

    useApolloErrorSnackbar(error);
    const catchLazyQueryError = useCatchLazyQueryError();

    const onFilterChange = React.useCallback(
        async (query: string) => {
            if (query.length > 2) {
                try {
                    const result = await getActivityType({
                        variables: { query, limit: 50 },
                    });
                    if (!result.data) {
                        return [];
                    }
                    if (!workerGender) {
                        throw new Error("Mission's worker info's gender is not defined");
                    }
                    const seen = new Set<string>();
                    return result.data.findActivityType.elements
                        .map((at) => ({
                            value: at.id,
                            label: formatActivityTypeTitle(at.title, workerGender),
                        }))
                        .filter((item) => {
                            if (seen.has(item.label)) return false;
                            seen.add(item.label);
                            return true;
                        })
                        .sort((a, b) => a.label.length - b.label.length);
                } catch (e: unknown) {
                    catchLazyQueryError(e as Error);
                }
                return [];
            } else {
                return null;
            }
        },
        [catchLazyQueryError, getActivityType, workerGender],
    );

    const jobTitlesData = useMemo(
        () =>
            !isPrivateClient
                ? onFilterChange
                : (privateClientActivityTypes.data?.getPrivateClientActivityTypes || [])
                      .filter(
                          (at, index, self) =>
                              self.findIndex((t) => t.activityTypeId === at.activityTypeId) ===
                              index,
                      )
                      .map((at) => ({
                          value: at.activityTypeId,
                          label: at.name,
                      })),
        [
            isPrivateClient,
            onFilterChange,
            privateClientActivityTypes.data?.getPrivateClientActivityTypes,
        ],
    );

    const setActivityType = React.useCallback(
        (value: Buildable<ActivityType>) => {
            setMission({
                ...mission,
                activityType: value,
            });
        },
        [mission, setMission],
    );

    const setPrivateJob = React.useCallback(
        (activityType: Buildable<ActivityType>, cctEditionId: string) => {
            // find cct by edition id
            const cct = allCCT?.find((cct) => cct.cctEditionId === cctEditionId);
            const newHoursPerWeek = cct?.hoursPerWeekDefault || DEFAULT_HOURS_PER_WEEK;
            setMission({
                ...mission,
                activityType: activityType,
                currentContract: {
                    ...mission.currentContract,
                    jobTitle: activityType.title,
                    cctContractId: cct?.cctContractId,
                    hoursPerWeekFullTime: newHoursPerWeek,
                    cctEditionId,
                },
                ijmSelection:
                    currentProfile?.client && cct
                        ? getIjmSelection(currentProfile?.client, cct, "all")
                        : undefined,
            });
        },
        [mission, setMission, allCCT, currentProfile?.client],
    );

    const addMissionDocument = React.useCallback(
        (document: DisplayableDocument) => {
            setMission((prev) => {
                const prevDocument = (prev.documents as Document[]) || [];
                return { ...prev, documents: [...prevDocument, document] };
            });
        },
        [setMission],
    );

    const saveDocument = React.useCallback(
        (document: DisplayableDocument) => {
            setMission((prev) => {
                const prevDocument = (prev.documents as Document[]) || [];
                const updatedDocuments = prevDocument.map((doc) =>
                    doc.id === document.id ? document : doc,
                );
                return { ...prev, documents: updatedDocuments };
            });
        },
        [setMission],
    );

    const deleteDocument = React.useCallback(
        (document: DisplayableDocument) => {
            setMission((prev) => {
                const prevDocument = (prev.documents as Document[]) || [];
                const updatedDocuments = prevDocument.filter((doc) => doc.id !== document.id);
                return { ...prev, documents: updatedDocuments };
            });
        },
        [setMission],
    );

    return (
        <>
            <VStack gap={1} v={0}>
                <BodyMediumText color={colors.greyAccessible}>
                    {i18n.t("screens.user.create_mission_job_title.description_top")}
                </BodyMediumText>

                <SelectField<string>
                    style={styles.items}
                    isDynamicHeight
                    label={i18n.t("screens.user.create_mission_job_title.job")}
                    value={
                        mission.activityType?.title && mission.workerInfo?.gender
                            ? !isPrivateClient
                                ? formatActivityTypeTitle(mission.activityType.title, workerGender)
                                : mission.activityType.id
                            : undefined
                    }
                    data={jobTitlesData}
                    searchPlaceholder={i18n.t("screens.user.create_mission_activity_type.search")}
                    onSelect={(selectedItem) => {
                        if (isPrivateClient) {
                            const selectedPrivateActivityType = (
                                privateClientActivityTypes.data?.getPrivateClientActivityTypes || []
                            ).find((at) => at.activityTypeId === selectedItem.value);
                            if (selectedPrivateActivityType) {
                                setPrivateJob(
                                    {
                                        id: selectedItem.value,
                                        title: selectedItem.label,
                                    },
                                    selectedPrivateActivityType.cctEditionId,
                                );
                            }
                        } else if (typeof selectedItem.value === "string") {
                            setActivityType({
                                id: selectedItem.value,
                                title: selectedItem.label,
                            });
                        }
                    }}
                    emptyResult={
                        <Callout
                            mode="warning"
                            markdown={i18n.t("screens.user.create_mission_activity_type.no_result")}
                        />
                    }
                />

                <VStack gap={0} v={0}>
                    <BodyMediumText>
                        {i18n.t("screens.user.create_mission_job_title.missionAnnexInfo", {
                            legalEmployerName: mission.legalEmployer?.marketingName,
                        })}
                    </BodyMediumText>
                    <Gap gap={0.5} />
                    <DocumentButton
                        asListAction="primary"
                        label={i18n.t("screens.user.create_mission_job_title.missionAnnexUpload")}
                        right="arrow-up-to-line"
                        onSelect={addMissionDocument}
                        documentType={DocumentType.MissionAnnex}
                        saveButtonTitle={i18n.t("import")}
                        enableCropZoom={false}
                        style={{
                            paddingVertical: 0,
                            marginVertical: 0,
                            marginLeft: -gridSize / 4,
                            marginRight: -gridSize / 2,
                        }}
                    />
                    <ListItem
                        labels={[
                            {
                                text: i18n.t(
                                    "screens.user.create_mission_job_title.missionAnnexAddInLine",
                                ),
                                mode: "cta",
                            },
                        ]}
                        onPress={() => {
                            const newDocumentId = uuidv4();
                            const newNote = {
                                id: newDocumentId,
                                title: "",
                                type: DocumentType.MissionAnnex,
                                inlineContent: "",
                                documentUrl: null,
                            };
                            editDocument({
                                document: newNote,
                                onSave: addMissionDocument,
                            });
                        }}
                        rightIcon="note"
                        rightActionMode="action_simple"
                        style={{
                            marginHorizontal: -gridSize,
                            paddingHorizontal: gridSize,
                            borderBottomWidth: mission.documents?.length ? 1 : 0,
                        }}
                    />
                    {mission.documents?.map((doc) => {
                        const document = doc as Document;
                        return (
                            <ListItem
                                key={document.id}
                                description={document.title}
                                style={{
                                    marginHorizontal: -gridSize,
                                    paddingHorizontal: gridSize,
                                }}
                                icon="memo"
                                onPress={() => {
                                    editDocument({
                                        document,
                                        onSave: saveDocument,
                                        onDelete: deleteDocument,
                                    });
                                }}
                            />
                        );
                    })}
                </VStack>
            </VStack>
        </>
    );
};

const useStyles = () => {
    const { fonts, colors, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                items: {
                    textAlign: "auto",
                    width: "auto",
                    alignSelf: "stretch",
                },
                cctView: {
                    marginTop: gridSize / 2,
                },
                cctTitle: {
                    ...fonts.regular,
                },
                cctDescription: {
                    ...fonts.bodySmall,
                    color: colors.fieldLabel,
                },
                cctDetailsItem: {
                    marginHorizontal: -(gridSize / 1.5),
                },
            }),
        [colors.fieldLabel, fonts.bodySmall, fonts.regular, gridSize],
    );
};

export default MissionJobTitle;
